import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { MessageSquare, FileSearch, FileEdit, Check, AlertTriangle, Send, Sparkles, Lock, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { pickBg } from "@/assets/generated";
import { EditorialHeading } from "./editorial-heading";

const DEMO_LIMIT = 2;
const VISITOR_KEY = "lexai_demo_visitor";

function getVisitorId(): string {
  if (typeof window === "undefined") return "ssr-placeholder";
  try {
    let v = window.localStorage.getItem(VISITOR_KEY);
    if (!v) {
      const rnd =
        (window.crypto as any)?.randomUUID?.() ??
        Math.random().toString(36).slice(2) + Date.now().toString(36);
      v = String(rnd).replace(/[^a-zA-Z0-9_-]/g, "").slice(0, 64) || "anon" + Date.now();
      window.localStorage.setItem(VISITOR_KEY, v);
    }
    return v;
  } catch {
    return "anon" + Date.now();
  }
}

const demoBg = pickBg(17);

const TAB_ICONS = { research: MessageSquare, analysis: FileSearch, drafting: FileEdit } as const;
type TabId = keyof typeof TAB_ICONS;

export function DemoSection() {
  const { t } = useTranslation();
  const [active, setActive] = useState<TabId>("research");
  const tabs: { id: TabId; label: string }[] = [
    { id: "research", label: t("demo.tabs.research") },
    { id: "analysis", label: t("demo.tabs.analysis") },
    { id: "drafting", label: t("demo.tabs.drafting") },
  ];
  return (
    <section id="demo" className="relative overflow-hidden bg-[hsl(var(--surface-raised))] py-16 sm:py-24 md:py-32">
      <img src={demoBg} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-10 dark:opacity-20" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--surface-raised))] via-[hsl(var(--surface-raised))]/85 to-[hsl(var(--surface-raised))]" />
      <div className="relative mx-auto max-w-6xl px-5 sm:px-6 lg:px-8">
        <EditorialHeading
          index="No. 03"
          eyebrow={t("demo.eyebrow")}
          title={t("demo.title")}
          subtitle={t("demo.subtitle")}
        />

        <div className="mt-12 flex justify-center">
          <div className="inline-flex rounded-xl border border-border bg-card p-1 shadow-sm">
            {tabs.map((tab) => {
              const Icon = TAB_ICONS[tab.id];
              return (
                <button
                  key={tab.id}
                  onClick={() => setActive(tab.id)}
                  className={cn(
                    "flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium transition-all",
                    active === tab.id ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <Icon className="h-4 w-4" /> {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-10 rounded-2xl border border-border bg-card p-4 shadow-xl sm:p-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
              className="min-h-[420px]"
            >
              {active === "research" && <ResearchDemo />}
              {active === "analysis" && <AnalysisDemo />}
              {active === "drafting" && <DraftingDemo />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Research ---------------- */

type PresetKey = "prescrizione" | "risarcimento" | "recesso";
const PRESET_KEYS: PresetKey[] = ["prescrizione", "risarcimento", "recesso"];

type Source = { icon: string; title: string; desc: string };
type Preset = { label: string; question: string; area: string; answer: string; sources: Source[] };

function ResearchDemo() {
  const { t, i18n } = useTranslation();
  const [preset, setPreset] = useState<PresetKey>("prescrizione");
  const [draft, setDraft] = useState("");
  const [custom, setCustom] = useState<null | { question: string; area: string; answer: string }>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<"network" | "rate" | "server" | null>(null);
  const [used, setUsed] = useState<number>(0);
  const [statusReady, setStatusReady] = useState(false);
  const visitorId = useRef<string>("");
  const lastQuestion = useRef<string>("");
  const retryCountRef = useRef(0);

  const presets = useMemo(() => {
    const map: Record<PresetKey, Preset> = {} as any;
    for (const k of PRESET_KEYS) {
      const obj = t(`demo.presets.${k}`, { returnObjects: true }) as Preset;
      map[k] = obj && typeof obj === "object" ? obj : { label: k, question: "", area: "", answer: "", sources: [] };
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.resolvedLanguage]);

  useEffect(() => {
    visitorId.current = getVisitorId();
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/public/demo-chat?visitorId=${encodeURIComponent(visitorId.current)}`);
        const json: any = await res.json().catch(() => ({}));
        if (!cancelled && json?.ok) setUsed(Math.min(json.used ?? 0, DEMO_LIMIT));
      } catch {
        /* offline */
      } finally {
        if (!cancelled) setStatusReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const limited = used >= DEMO_LIMIT;
  const remaining = Math.max(0, DEMO_LIMIT - used);

  const current = useMemo(() => {
    if (custom) return { ...custom, sources: presets[preset].sources };
    return presets[preset];
  }, [custom, preset, presets]);

  const submit = useCallback(async (question: string, isRetry = false) => {
    if (!question || limited) return;
    setLoading(true);
    setError(null);
    setErrorKind(null);
    try {
      const res = await fetch("/api/public/demo-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question, visitorId: visitorId.current, locale: i18n.resolvedLanguage }),
      });
      const json: any = await res.json().catch(() => ({}));

      if (!res.ok || !json?.ok) {
        if (typeof json?.used === "number") setUsed(Math.min(json.used, DEMO_LIMIT));
        if (json?.limited || res.status === 429) {
          setUsed(DEMO_LIMIT);
          setError(t("demo.errors.exhausted"));
          setErrorKind("rate");
        } else if (res.status >= 500) {
          if (!isRetry && retryCountRef.current < 1) {
            retryCountRef.current += 1;
            await new Promise((r) => setTimeout(r, 800));
            await submit(question, true);
            return;
          }
          setError(t("demo.errors.service"));
          setErrorKind("server");
        } else {
          setError(t("demo.errors.generic"));
          setErrorKind("server");
        }
        return;
      }

      retryCountRef.current = 0;
      setCustom({ question, area: t("demo.customArea"), answer: json.answer });
      if (typeof json.used === "number") setUsed(Math.min(json.used, DEMO_LIMIT));
      else setUsed((u) => Math.min(u + 1, DEMO_LIMIT));
      setDraft("");
    } catch {
      if (!isRetry && retryCountRef.current < 1) {
        retryCountRef.current += 1;
        await new Promise((r) => setTimeout(r, 800));
        await submit(question, true);
        return;
      }
      setError(t("demo.errors.offline"));
      setErrorKind("network");
    } finally {
      setLoading(false);
    }
  }, [limited, i18n.resolvedLanguage, t]);

  const sendCustom = () => {
    const q = draft.trim();
    if (!q) return;
    lastQuestion.current = q;
    retryCountRef.current = 0;
    submit(q);
  };

  const retry = () => {
    if (!lastQuestion.current) return;
    retryCountRef.current = 0;
    submit(lastQuestion.current);
  };

  return (
    <div className="grid gap-4 md:grid-cols-[1fr_2.5fr]">
      <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-3">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("demo.today")}</div>
        {PRESET_KEYS.map((k) => {
          const isActive = !custom && preset === k;
          return (
            <button
              key={k}
              type="button"
              onClick={() => { setCustom(null); setPreset(k); }}
              className={cn(
                "block w-full rounded-md px-2 py-1.5 text-left text-xs transition-colors",
                isActive ? "bg-primary/10 font-medium text-primary" : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
              )}
            >
              {presets[k].label}
            </button>
          );
        })}
        <div className="mt-3 rounded-md border border-border bg-card/50 p-2 text-[10px] text-muted-foreground">
          {t("demo.freeQuestions")}
          <div className="mt-0.5 font-mono text-sm font-semibold text-foreground">
            {statusReady ? `${remaining}/${DEMO_LIMIT}` : "-/2"}
          </div>
        </div>
      </div>
      <div className="space-y-3">
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("demo.question")}</div>
          <p className="mt-1 text-sm">{current.question}</p>
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={current.question}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="rounded-lg border border-accent/30 bg-accent-muted/30 p-4"
          >
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
              <span className="flex h-5 w-5 items-center justify-center rounded bg-primary text-[10px] text-primary-foreground">L</span>
              LexAIPro · {current.area}
            </div>
            {loading ? (
              <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                <Sparkles className="h-4 w-4 animate-pulse" /> {t("demo.searching")}
              </div>
            ) : custom ? (
              <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed">{current.answer}</p>
            ) : (
              <p className="mt-2 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: current.answer }} />
            )}
            {!custom && (
              <div className="mt-3 space-y-2">
                {(current.sources ?? []).map((s) => (
                  <div key={s.title} className="rounded-md border border-border bg-card p-2.5 text-xs">
                    {s.icon} <strong>{s.title}</strong> - {s.desc}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {error && errorKind !== "rate" && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <div className="flex-1">
              <div>{error}</div>
              {lastQuestion.current && (
                <button
                  type="button"
                  onClick={retry}
                  disabled={loading}
                  className="mt-1.5 inline-flex items-center gap-1 rounded border border-destructive/40 bg-card px-2 py-1 text-[11px] font-medium text-destructive transition hover:bg-destructive/10 disabled:opacity-50"
                >
                  <RefreshCw className="h-3 w-3" /> {t("demo.retry")}
                </button>
              )}
            </div>
          </div>
        )}

        {limited ? (
          <div className="rounded-lg border border-primary/30 bg-gradient-to-br from-primary/10 to-accent/10 p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                <Lock className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-foreground">{t("demo.exhaustedTitle")}</div>
                <p className="mt-1 text-xs text-muted-foreground">{t("demo.exhaustedDesc")}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link
                    to="/signup"
                    className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition hover:bg-primary/90"
                  >
                    {t("demo.startFree")}
                  </Link>
                  <a
                    href="/#pricing"
                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition hover:bg-muted"
                  >
                    {t("demo.seePlans")}
                  </a>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <form
            onSubmit={(e) => { e.preventDefault(); sendCustom(); }}
            className="flex items-center gap-2 rounded-lg border border-border bg-card p-2"
          >
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={t("demo.inputPlaceholder")}
              maxLength={500}
              disabled={loading || !statusReady}
              className="flex-1 bg-transparent px-2 py-1.5 text-sm outline-none placeholder:text-muted-foreground disabled:opacity-60"
            />
            <span className="hidden sm:inline font-mono text-[10px] text-muted-foreground">
              {statusReady ? `${remaining}/${DEMO_LIMIT}` : "…"}
            </span>
            <button
              type="submit"
              disabled={!draft.trim() || loading || !statusReady || draft.trim().length < 5}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? <Sparkles className="h-3.5 w-3.5 animate-pulse" /> : <Send className="h-3.5 w-3.5" />}
              {loading ? t("demo.sending") : t("demo.ask")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

/* ---------------- Analysis ---------------- */

type Clause = { id: string; level: "ok" | "warn" | "risk"; text: string; suffix: string; title: string; note: string };

function AnalysisDemo() {
  const { t, i18n } = useTranslation();
  const clauses = useMemo(() => {
    const v = t("demo.analysis.clauses", { returnObjects: true }) as Clause[];
    return Array.isArray(v) ? v : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.resolvedLanguage]);

  const [selected, setSelected] = useState<string>("c3");
  const current = clauses.find((c) => c.id === selected) ?? clauses[0];
  if (!current) return null;

  const colorByLevel: Record<Clause["level"], string> = {
    ok: "bg-success/20",
    warn: "bg-warning/30",
    risk: "bg-destructive/20",
  };
  const tone =
    current.level === "ok"
      ? { icon: Check, accent: "border-success bg-success/5 text-success", iconClass: "text-success" }
      : current.level === "warn"
      ? { icon: AlertTriangle, accent: "border-warning bg-warning/5", iconClass: "text-[hsl(var(--warning))]" }
      : { icon: AlertTriangle, accent: "border-destructive bg-destructive/5", iconClass: "text-destructive" };
  const Icon = tone.icon;
  const risk = current.level === "ok" ? 20 : current.level === "warn" ? 60 : 90;
  const riskLabel =
    current.level === "ok" ? t("demo.analysis.risk.low")
    : current.level === "warn" ? t("demo.analysis.risk.med")
    : t("demo.analysis.risk.high");
  const riskBg = current.level === "ok" ? "bg-success/20 text-success" : current.level === "warn" ? "bg-warning/20 text-[hsl(var(--warning))]" : "bg-destructive/20 text-destructive";

  return (
    <div className="grid gap-4 md:grid-cols-[2fr_1.5fr]">
      <div className="rounded-lg border border-border bg-card p-5 text-sm leading-relaxed">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-3">{t("demo.analysis.docMeta")}</div>
        {clauses.map((c, i) => (
          <p key={c.id} className={i > 0 ? "mt-3" : ""}>
            {i + 1}.{" "}
            <button
              type="button"
              onClick={() => setSelected(c.id)}
              className={cn(
                "rounded px-1 text-left transition-all",
                colorByLevel[c.level],
                selected === c.id ? "ring-2 ring-primary/50" : "hover:ring-1 hover:ring-primary/30"
              )}
            >
              {c.text}
            </button>
            {c.suffix}
          </p>
        ))}
      </div>
      <div className="space-y-3">
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider">{t("demo.analysis.riskScore")}</span>
            <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-bold", riskBg)}>{riskLabel}</span>
          </div>
          <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden">
            <motion.div
              className={cn(
                "h-full",
                current.level === "ok" ? "bg-success" : current.level === "warn" ? "gradient-gold" : "bg-destructive"
              )}
              initial={false}
              animate={{ width: `${risk}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, x: 8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={cn("rounded-lg border-l-4 p-3 text-xs", tone.accent)}
          >
            <div className="flex items-start gap-2">
              <Icon className={cn("h-4 w-4 shrink-0", tone.iconClass)} />
              <div>
                <strong>{current.title}</strong>
                <p className="mt-1 text-muted-foreground">{current.note}</p>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ---------------- Drafting ---------------- */

function DraftingDemo() {
  const { t } = useTranslation();
  const [vars, setVars] = useState({ NOME_PARTE_A: "", CF_A: "", IMPORTO: "", DATA: "" });
  const [hasNDA, setHasNDA] = useState(false);

  const set = (k: keyof typeof vars, v: string) => setVars((s) => ({ ...s, [k]: v }));
  const display = (k: keyof typeof vars, placeholder: string) =>
    vars[k] ? <span className="bg-success/20 px-1 rounded">{vars[k]}</span> : <span className="bg-accent/20 px-1 rounded">[{placeholder}]</span>;

  return (
    <div className="grid gap-4 md:grid-cols-[1.5fr_2fr]">
      <div className="rounded-lg border border-border bg-card p-5 font-mono text-xs leading-relaxed">
        <div className="text-foreground/90">
          <div className="font-serif text-base text-center font-bold mb-3 not-italic">{t("demo.drafting.docTitle")}</div>
          <p>
            {t("demo.drafting.intro").split("{{a}}")[0]}
            {display("NOME_PARTE_A", "NOME_PARTE_A")}
            {t("demo.drafting.intro").split("{{a}}")[1]?.split("{{b}}")[0]}
            {display("CF_A", "CF_A")}
            {t("demo.drafting.intro").split("{{b}}")[1]}
          </p>
          <p className="mt-2"><strong>{t("demo.drafting.art1Title")}</strong></p>
          <p>{t("demo.drafting.art1")}</p>
          <p className="mt-2"><strong>{t("demo.drafting.art2Title")}</strong></p>
          <p>
            {t("demo.drafting.art2").split("{{importo}}")[0]}
            {display("IMPORTO", "IMPORTO")}
            {t("demo.drafting.art2").split("{{importo}}")[1]}
          </p>
          <p className="mt-2"><strong>{t("demo.drafting.art3Title")}</strong></p>
          <p>
            {t("demo.drafting.art3").split("{{data}}")[0]}
            {display("DATA", "DATA")}
            {t("demo.drafting.art3").split("{{data}}")[1]}
          </p>
          <AnimatePresence>
            {hasNDA && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <p className="mt-2"><strong>{t("demo.drafting.art4Title")}</strong></p>
                <p>{t("demo.drafting.art4")}</p>
              </motion.div>
            )}
          </AnimatePresence>
          <span className="inline-block w-1 h-3 bg-primary animate-pulse" />
        </div>
      </div>
      <div className="space-y-3">
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-primary">{t("demo.drafting.aiSuggestion")}</div>
          {hasNDA ? (
            <p className="mt-2 text-sm text-success">{t("demo.drafting.ndaAdded")}</p>
          ) : (
            <>
              <p className="mt-2 text-sm">{t("demo.drafting.ndaPrompt")}</p>
              <button
                onClick={() => setHasNDA(true)}
                className="mt-3 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition hover:bg-primary/90"
              >
                {t("demo.drafting.ndaCta")}
              </button>
            </>
          )}
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-primary">{t("demo.drafting.variables")}</div>
          <div className="mt-2 space-y-2 text-xs">
            {([
              ["NOME_PARTE_A", "Mario Rossi"],
              ["CF_A", "RSSMRA80A01H501U"],
              ["IMPORTO", "5.000,00"],
              ["DATA", "01/01/2026"],
            ] as const).map(([k, ph]) => (
              <div key={k} className="flex items-center gap-2">
                <span className="w-32 shrink-0 text-muted-foreground">[{k}]</span>
                <input
                  value={vars[k]}
                  onChange={(e) => set(k, e.target.value)}
                  placeholder={ph}
                  className="flex-1 rounded border border-border bg-background px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-primary/40"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
